import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import AdminLayout from "@/components/admin/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { ArrowLeft, Plus, Trash2, Save, Sparkles } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";

interface QuizQuestion {
  id?: string;
  question: string;
  options: string[];
  correct_answer: number;
  explanation: string;
  order_index: number;
}

const QuizManager = () => {
  const { blogPostId } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [blogPost, setBlogPost] = useState<any>(null);
  const [quizId, setQuizId] = useState<string | null>(null);
  const [questions, setQuestions] = useState<QuizQuestion[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);

  const [formData, setFormData] = useState<QuizQuestion>({
    question: "",
    options: ["", "", "", ""],
    correct_answer: 0,
    explanation: "",
    order_index: 0,
  });

  useEffect(() => {
    if (blogPostId) {
      fetchBlogPost();
      fetchQuiz();
    }
  }, [blogPostId]);

  const fetchBlogPost = async () => {
    const { data, error } = await supabase
      .from("blog_posts")
      .select("*")
      .eq("id", blogPostId)
      .single();

    if (error) {
      toast({
        title: "Error",
        description: "Failed to load blog post",
        variant: "destructive",
      });
      return;
    }

    setBlogPost(data);
  };

  const fetchQuiz = async () => {
    setLoading(true);
    try {
      const { data: quizData, error: quizError } = await supabase
        .from("quizzes")
        .select("id")
        .eq("blog_post_id", blogPostId)
        .maybeSingle();

      if (quizError && quizError.code !== "PGRST116") {
        throw quizError;
      }

      if (quizData) {
        setQuizId(quizData.id);
        const { data: questionsData, error: questionsError } = await supabase
          .from("quiz_questions")
          .select("*")
          .eq("quiz_id", quizData.id)
          .order("order_index");

        if (questionsError) throw questionsError;
        
        const formattedQuestions = (questionsData || []).map(q => ({
          id: q.id,
          question: q.question,
          options: q.options as string[],
          correct_answer: q.correct_answer,
          explanation: q.explanation,
          order_index: q.order_index,
        }));
        
        setQuestions(formattedQuestions);
      }
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateQuestions = async () => {
    if (!blogPost) return;

    setGenerating(true);
    try {
      const { data, error } = await supabase.functions.invoke("generate-quiz-questions", {
        body: {
          blogContent: blogPost.content,
          blogTitle: blogPost.title,
          numberOfQuestions: 5,
        },
      });

      if (error) throw error;

      if (data.questions) {
        setQuestions(data.questions);
        toast({
          title: "Success!",
          description: `Generated ${data.questions.length} quiz questions`,
        });
      }
    } catch (error: any) {
      console.error("Quiz generation error:", error);
      
      // Check if it's a FunctionsHttpError with a response
      let errorMessage = "Failed to generate quiz questions";
      let errorTitle = "Error";
      
      if (error?.context?.body) {
        const errorBody = error.context.body;
        
        if (errorBody.error?.includes("Rate limit exceeded")) {
          errorTitle = "Rate Limit Exceeded";
          errorMessage = "You've made too many requests. Please wait a few minutes and try again. The AI service has usage limits to ensure fair access.";
        } else if (errorBody.error?.includes("credits exhausted") || errorBody.error?.includes("Payment required")) {
          errorTitle = "AI Credits Exhausted";
          errorMessage = "Your AI credits have been used up. Please add more credits in Settings → Workspace → Usage to continue generating quizzes.";
        } else {
          errorMessage = errorBody.error || errorMessage;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast({
        title: errorTitle,
        description: errorMessage,
        variant: "destructive",
        duration: 6000, // Show longer for important messages
      });
    } finally {
      setGenerating(false);
    }
  };

  const handleAddQuestion = () => {
    setQuestions([
      ...questions,
      {
        ...formData,
        order_index: questions.length,
      },
    ]);
    resetForm();
  };

  const handleEditQuestion = (index: number) => {
    setEditingIndex(index);
    setFormData(questions[index]);
  };

  const handleUpdateQuestion = () => {
    if (editingIndex === null) return;
    const updated = [...questions];
    updated[editingIndex] = formData;
    setQuestions(updated);
    setEditingIndex(null);
    resetForm();
  };

  const handleDeleteQuestion = (index: number) => {
    setQuestions(questions.filter((_, i) => i !== index));
  };

  const resetForm = () => {
    setFormData({
      question: "",
      options: ["", "", "", ""],
      correct_answer: 0,
      explanation: "",
      order_index: questions.length,
    });
  };

  const handleSaveQuiz = async () => {
    if (questions.length === 0) {
      toast({
        title: "Error",
        description: "Please add at least one question",
        variant: "destructive",
      });
      return;
    }

    setSaving(true);
    try {
      let currentQuizId = quizId;

      // Create quiz if it doesn't exist
      if (!currentQuizId) {
        const { data: newQuiz, error: quizError } = await supabase
          .from("quizzes")
          .insert({ blog_post_id: blogPostId })
          .select()
          .single();

        if (quizError) throw quizError;
        currentQuizId = newQuiz.id;
        setQuizId(currentQuizId);
      }

      // Delete existing questions
      await supabase.from("quiz_questions").delete().eq("quiz_id", currentQuizId);

      // Insert new questions
      const questionsToInsert = questions.map((q, index) => ({
        quiz_id: currentQuizId,
        question: q.question,
        options: q.options,
        correct_answer: q.correct_answer,
        explanation: q.explanation,
        order_index: index,
      }));

      const { error: insertError } = await supabase
        .from("quiz_questions")
        .insert(questionsToInsert);

      if (insertError) throw insertError;

      toast({
        title: "Success!",
        description: "Quiz saved successfully",
      });

      navigate("/admin/posts");
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="p-8 text-center">Loading...</div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate("/admin/posts")}>
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div>
              <h1 className="text-3xl font-bold">Quiz Manager</h1>
              {blogPost && (
                <p className="text-muted-foreground">{blogPost.title}</p>
              )}
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={handleGenerateQuestions}
              disabled={generating || !blogPost}
              variant="outline"
            >
              <Sparkles className="mr-2 h-4 w-4" />
              {generating ? "Generating..." : "Generate with AI"}
            </Button>
            <Button onClick={handleSaveQuiz} disabled={saving || questions.length === 0}>
              <Save className="mr-2 h-4 w-4" />
              {saving ? "Saving..." : "Save Quiz"}
            </Button>
          </div>
        </div>

        {/* Question Form */}
        <Card>
          <CardHeader>
            <CardTitle>{editingIndex !== null ? "Edit Question" : "Add New Question"}</CardTitle>
            <CardDescription>
              Create engaging quiz questions to test reader comprehension
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="question">Question</Label>
              <Textarea
                id="question"
                value={formData.question}
                onChange={(e) => setFormData({ ...formData, question: e.target.value })}
                placeholder="What is the main concept discussed in the article?"
              />
            </div>

            <div className="space-y-2">
              <Label>Answer Options</Label>
              {formData.options.map((option, i) => (
                <div key={i} className="flex gap-2">
                  <Input
                    value={option}
                    onChange={(e) => {
                      const newOptions = [...formData.options];
                      newOptions[i] = e.target.value;
                      setFormData({ ...formData, options: newOptions });
                    }}
                    placeholder={`Option ${i + 1}`}
                  />
                  <Button
                    variant={formData.correct_answer === i ? "default" : "outline"}
                    onClick={() => setFormData({ ...formData, correct_answer: i })}
                  >
                    {formData.correct_answer === i ? "Correct" : "Mark Correct"}
                  </Button>
                </div>
              ))}
            </div>

            <div>
              <Label htmlFor="explanation">Explanation</Label>
              <Textarea
                id="explanation"
                value={formData.explanation}
                onChange={(e) => setFormData({ ...formData, explanation: e.target.value })}
                placeholder="Explain why this is the correct answer..."
              />
            </div>

            <div className="flex gap-2">
              {editingIndex !== null ? (
                <>
                  <Button onClick={handleUpdateQuestion}>Update Question</Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setEditingIndex(null);
                      resetForm();
                    }}
                  >
                    Cancel
                  </Button>
                </>
              ) : (
                <Button onClick={handleAddQuestion}>
                  <Plus className="mr-2 h-4 w-4" />
                  Add Question
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Questions List */}
        {questions.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Quiz Questions ({questions.length})</CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12">#</TableHead>
                    <TableHead>Question</TableHead>
                    <TableHead className="w-32">Correct Answer</TableHead>
                    <TableHead className="w-32">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {questions.map((q, index) => (
                    <TableRow key={index}>
                      <TableCell>{index + 1}</TableCell>
                      <TableCell className="font-medium">{q.question}</TableCell>
                      <TableCell>
                        <Badge variant="secondary">{q.options[q.correct_answer]}</Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEditQuestion(index)}
                          >
                            Edit
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteQuestion(index)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}
      </div>
    </AdminLayout>
  );
};

export default QuizManager;
